import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getClientsByRut, registerClient } from '../api';
import { CLIENT_TYPE_OPTIONS } from '../utils/clientType';

const ModalQuotationClientPicker = ({
  isOpen,
  onClose,
  setClientId,
  setName,
  setRut,
  setEmail,
  setAddress,
  setCity,
  setPhone,
  clientType,
  setClientType,
  clientTypeOptions = CLIENT_TYPE_OPTIONS,
  canReadClient = true,
  canCreateClient = true,
}) => {
  const branchId = useParams().branchId || localStorage.getItem('selectedBranchId');
  const [searchRut, setSearchRut] = useState('');
  const [foundClient, setFoundClient] = useState(null);
  const [typeError, setTypeError] = useState('');
  const [stage, setStage] = useState('search'); // 'search' | 'typeOnly' | 'register'
  const [newClientData, setNewClientData] = useState({
    name: '',
    email: '',
    address: '',
    city: '',
    phone: '',
  });
  const searchDebounceRef = useRef(null);
  const lookupRequestIdRef = useRef(0);
  const lastLookupRutRef = useRef('');

  const renderTypeTabs = () => (
    <div className="flex flex-wrap gap-1 border-b border-gray-200">
      {clientTypeOptions.map((option) => {
        const isActive = clientType === option.value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => {
              setTypeError('');
              setClientType(option.value);
            }}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px rounded-t-md transition ${
              isActive
                ? 'bg-blue-50 text-blue-700 border-blue-600'
                : 'bg-transparent text-gray-600 border-transparent hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );

  const formatRut = (value) =>
    value
      .replace(/[^0-9kK]/g, '')
      .replace(/^(\d{1,2})(\d{3})(\d{3})([0-9kK])$/, '$1.$2.$3-$4')
      .toUpperCase();

  const normalizeRutForLookup = (value) =>
    String(value || '').replace(/[^0-9kK]/g, '').toUpperCase();

  const clearSelectedClient = useCallback(() => {
    setFoundClient(null);
    setClientId(null);
    setName('');
    setRut('');
    setEmail('');
    setAddress('');
    setCity('');
    setPhone('');
  }, [setAddress, setCity, setClientId, setEmail, setName, setPhone, setRut]);

  const resetFields = () => {
    setClientId(null);
    setName('');
    setRut('');
    setEmail('');
    setAddress('');
    setCity('');
    setPhone('');
    setFoundClient(null);
    setStage('search');
    setTypeError('');
    setNewClientData({ name: '', email: '', address: '', city: '', phone: '' });
  };

  const applyFoundClient = useCallback((client) => {
    if (!client) return;
    setFoundClient(client);
    setClientId(client.id);
    setName(client.name || '');
    setRut(client.rut || '');
    setEmail(client.email || '');
    setAddress(client.address || '');
    setCity(client.city || '');
    setPhone(client.phone || '');
    setTypeError('');
    setStage('typeOnly');
  }, [setAddress, setCity, setClientId, setEmail, setName, setPhone, setRut]);

  const lookupClientByRut = useCallback(async (rutValue, options = {}) => {
    if (!canReadClient) return false;
    const { openRegisterOnMiss = true, clearSelectionOnMiss = false } = options;
    const normalizedRut = normalizeRutForLookup(rutValue);
    if (normalizedRut.length < 7) return false;
    const requestId = ++lookupRequestIdRef.current;
    try {
      const clients = await getClientsByRut(rutValue);
      if (requestId !== lookupRequestIdRef.current) return false;
      const client = clients?.[0];
      if (client) {
        applyFoundClient(client);
        return true;
      }
      if (clearSelectionOnMiss) {
        clearSelectedClient();
      } else {
        setFoundClient(null);
      }
      if (openRegisterOnMiss) {
        setStage('register');
      }
      return false;
    } catch (err) {
      if (requestId === lookupRequestIdRef.current) {
        console.error(err);
      }
      return false;
    }
  }, [applyFoundClient, canReadClient, clearSelectedClient]);

  const handleSearchByRut = async () => {
    await lookupClientByRut(searchRut, { openRegisterOnMiss: true, clearSelectionOnMiss: false });
  };

  useEffect(() => {
    if (!isOpen) return;
    if (!canReadClient) return;
    const normalizedRut = normalizeRutForLookup(searchRut);
    if (normalizedRut.length < 7) {
      lastLookupRutRef.current = '';
      return;
    }
    if (lastLookupRutRef.current === normalizedRut) {
      return;
    }
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      lastLookupRutRef.current = normalizedRut;
      lookupClientByRut(searchRut, { openRegisterOnMiss: true, clearSelectionOnMiss: true });
    }, 350);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [isOpen, searchRut, canReadClient, lookupClientByRut]);

  useEffect(() => {
    if (isOpen) return;
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    lastLookupRutRef.current = '';
    lookupRequestIdRef.current += 1;
  }, [isOpen]);

  const handleContinueWithoutClient = () => {
    setFoundClient(null);
    setStage('typeOnly');
  };

  const handleContinueWithoutClientTypeOnly = () => {
    if (!clientType) {
      setTypeError('Selecciona un tipo de venta.');
      return;
    }
    setTypeError('');
    resetFields();
    onClose(false);
  };

  const handleAssignTypeAndClose = async () => {
    if (!clientType) {
      setTypeError('Selecciona un tipo de venta.');
      return;
    }
    setTypeError('');

    if (foundClient) {
      onClose(false);
      return;
    }

    // Registrar cliente nuevo
    if (!newClientData.name || !searchRut) {
      setTypeError('Completa RUT y nombre para registrar.');
      return;
    }
    if (!canCreateClient) {
      setTypeError('No tienes permisos para registrar clientes.');
      return;
    }
    try {
      const resp = await registerClient({
        name: newClientData.name,
        rut: searchRut,
        email: newClientData.email,
        address: newClientData.address,
        city: newClientData.city,
        phone: newClientData.phone,
        branchId,
      });
      const c = resp.client;
      if (c) {
        setClientId(c.id);
        setName(c.name || '');
        setRut(c.rut || '');
        setEmail(c.email || '');
        setAddress(c.address || '');
        setCity(c.city || '');
        setPhone(c.phone || '');
      }
    } catch (err) {
      console.error('No se pudo registrar el cliente', err);
      setTypeError('No se pudo registrar el cliente.');
      return;
    }
    onClose(false);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-50"
      onClick={(event) => event.stopPropagation()}
    >
      <div
        className="bg-white w-full md:w-1/2 rounded-lg shadow"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 bg-gray-200 rounded-t-lg flex justify-between items-center">
          <h2 className="text-xl font-bold m-0">Seleccionar cliente</h2>
        </div>
        <div className="p-6 space-y-4">
          {stage === 'search' && (
            <>
              <div className="grid grid-cols-1 gap-3">
                <input
                  type="text"
                  placeholder="RUT"
                  value={searchRut}
                  onChange={(e) => setSearchRut(formatRut(e.target.value))}
                  className="p-3 border border-gray-300 rounded-lg"
                  autoComplete="off"
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleSearchByRut}
                    disabled={searchRut.length < 7 || !canReadClient}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-60"
                  >
                    Buscar cliente
                  </button>
                  <button
                    onClick={handleContinueWithoutClient}
                    className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 text-sm"
                  >
                    Continuar sin cliente
                  </button>
                </div>
                {!canReadClient && (
                  <p className="text-xs text-red-600">
                    No tienes permisos para buscar clientes.
                  </p>
                )}
              </div>

              {foundClient && (
                <div className="border rounded p-3 bg-gray-50 text-sm space-y-1">
                  <p className="font-semibold">{foundClient.name}</p>
                  <p>RUT: {foundClient.rut || '—'}</p>
                  <p>Email: {foundClient.email || '—'}</p>
                </div>
              )}
            </>
          )}

          {stage === 'typeOnly' && (
            <>
              {foundClient && (
                <div className="border rounded p-3 bg-gray-50 text-sm space-y-1">
                  <p className="font-semibold">{foundClient.name}</p>
                  <p>RUT: {foundClient.rut || '—'}</p>
                  <p>Email: {foundClient.email || '—'}</p>
                </div>
              )}

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium">Tipo de venta</label>
                {renderTypeTabs()}
                {typeError && <p className="text-xs text-red-600">{typeError}</p>}
              </div>

              <div className="flex flex-wrap gap-2 justify-end">
                <button
                  onClick={() => { setStage('search'); setFoundClient(null); }}
                  className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300 text-sm"
                >
                  Volver
                </button>
                {!foundClient && (
                  <button
                    onClick={handleContinueWithoutClientTypeOnly}
                    className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                  >
                    Continuar
                  </button>
                )}
                {foundClient && (
                  <button
                    onClick={handleAssignTypeAndClose}
                    className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                  >
                    Usar cliente
                  </button>
                )}
              </div>
            </>
          )}

          {stage === 'register' && (
            <>
              <div className="border rounded p-3 bg-gray-50 text-sm space-y-1">
                <p className="font-semibold">Nuevo cliente (no encontrado)</p>
                <p>RUT: {searchRut || '—'}</p>
              </div>
              {!canCreateClient && (
                <p className="text-xs text-red-600">
                  No tienes permisos para registrar clientes.
                </p>
              )}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium">Tipo de venta</label>
                {renderTypeTabs()}
                {typeError && <p className="text-xs text-red-600">{typeError}</p>}
              </div>
              <div className="grid grid-cols-1 gap-2">
                <input
                  type="text"
                  placeholder="Nombre (obligatorio para registrar)"
                  value={newClientData.name}
                  onChange={(e) => setNewClientData({ ...newClientData, name: e.target.value })}
                  className="border rounded p-2"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={newClientData.email}
                  onChange={(e) => setNewClientData({ ...newClientData, email: e.target.value })}
                  className="border rounded p-2"
                />
                <input
                  type="text"
                  placeholder="Dirección"
                  value={newClientData.address}
                  onChange={(e) => setNewClientData({ ...newClientData, address: e.target.value })}
                  className="border rounded p-2"
                />
                <input
                  type="text"
                  placeholder="Ciudad"
                  value={newClientData.city}
                  onChange={(e) => setNewClientData({ ...newClientData, city: e.target.value })}
                  className="border rounded p-2"
                />
                <input
                  type="text"
                  placeholder="Teléfono"
                  value={newClientData.phone}
                  onChange={(e) => setNewClientData({ ...newClientData, phone: e.target.value })}
                  className="border rounded p-2"
                />
              </div>
              <div className="flex flex-wrap gap-2 justify-end">
                <button
                  onClick={() => { setStage('search'); setFoundClient(null); }}
                  className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300 text-sm"
                >
                  Volver
                </button>
                <button
                  onClick={() => { setTypeError(''); setFoundClient(null); setStage('typeOnly'); }}
                  className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                >
                  Continuar sin cliente
                </button>
                {canCreateClient && (
                  <button
                    onClick={handleAssignTypeAndClose}
                    className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                  >
                    Registrar y continuar
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalQuotationClientPicker;
